"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { gsap } from "gsap";
import { navLinks } from "@/constants/navigation";
import { useScrollLock } from "@/hooks/useScrollLock";

interface CurtainMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CurtainMenu({ isOpen, onClose }: CurtainMenuProps) {
  useScrollLock(isOpen);

  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    const panel = panelRef.current;
    if (!root || !panel) return;

    const items = panel.querySelectorAll(".anim-item");

    tlRef.current = gsap.timeline({
      paused: true,
      defaults: { ease: "expo.inOut", duration: 1 },
    });

    tlRef.current
      .set(root, { display: "flex" })
      .fromTo(panel, { xPercent: 100 }, { xPercent: 0 })
      .fromTo(
        items,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.04,
          duration: 0.6,
          ease: "power4.out",
        },
        "-=0.5"
      );

    return () => {
      tlRef.current?.kill();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      tlRef.current?.play();
    } else {
      tlRef.current?.reverse().eventCallback("onReverseComplete", () => {
        gsap.set(rootRef.current, { display: "none" });
      });
    }
  }, [isOpen]);

  return (
    <div
      ref={rootRef}
      style={{ display: "none" }}
      className="fixed inset-0 z-[1000] overflow-hidden font-sans"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      {/* ── Backdrop ── */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* ══════════════════════════════════════════
          MAIN PANEL — slides in from right
      ══════════════════════════════════════════ */}
      <div
        ref={panelRef}
        className="absolute inset-0 flex flex-col overflow-hidden bg-[#060606] text-white md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >

        {/* ══════════════════════════════════════════
            MOBILE VIEW  — shown below md
        ══════════════════════════════════════════ */}
        <div className="flex h-full w-full flex-col md:hidden">

          {/* Ambient glow top */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-20 left-1/2 h-60 w-60 -translate-x-1/2 rounded-full bg-amber-500/8 blur-3xl"
          />

          {/* Ambient glow bottom */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-20 right-0 h-60 w-60 rounded-full bg-amber-500/5 blur-3xl"
          />

          {/* ── Top bar ── */}
          <div className="flex shrink-0 items-center justify-between px-7 pt-12 pb-8 xs:px-8 sm:px-10 sm:pt-14">
            {/* Brand */}
            <div className="anim-item space-y-0.5">
              <p className="text-[9px] font-bold uppercase tracking-[0.5em] text-amber-500 xs:text-[10px]">
                Quality Collections
              </p>
              <p className="text-base font-black uppercase tracking-tight text-white xs:text-lg sm:text-xl">
                Artisan Exports
              </p>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="anim-item group relative flex h-10 w-10 items-center justify-center xs:h-11 xs:w-11"
            >
              <span className="absolute inset-0 rounded-full border border-white/10 transition-all duration-300 group-hover:border-amber-500/40 group-hover:bg-amber-500/5" />
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                className="relative z-10 text-white/70 transition-colors group-hover:text-amber-500"
              >
                <line x1="1" y1="1" x2="13" y2="13" />
                <line x1="13" y1="1" x2="1" y2="13" />
              </svg>
            </button>
          </div>

          {/* ── Nav ── fills remaining height ── */}
          <nav
            aria-label="Main navigation"
            className="flex flex-1 flex-col justify-center px-7 xs:px-8 sm:px-10"
          >
            <ul className="space-y-0 divide-y divide-white/[0.04]">
              {navLinks.map((link, index) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    onClick={onClose}
                    className="anim-item group flex items-center justify-between py-4 xs:py-5 sm:py-6"
                  >
                    <div className="flex items-center gap-4">
                      <span className="w-6 text-[9px] font-bold tabular-nums text-white/20 xs:text-[10px]">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-2xl font-black uppercase tracking-tight text-white transition-colors duration-200 group-hover:text-amber-400 xs:text-3xl sm:text-4xl">
                        {link.label}
                      </span>
                    </div>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0 text-white/15 transition-all duration-200 group-hover:translate-x-1 group-hover:text-amber-400"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Footer ── */}
          <div className="shrink-0 px-7 pb-10 pt-6 xs:px-8 xs:pb-12 sm:px-10 sm:pb-14">

            {/* Top divider */}
            <div className="mb-6 h-px w-full bg-white/5" />

            <div className="flex items-end justify-between gap-4">
              {/* Left: address */}
              <div className="anim-item space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-px w-4 bg-amber-500/50" />
                  <span className="text-[8px] font-bold uppercase tracking-[0.4em] text-amber-500/70 xs:text-[9px]">
                    Export House
                  </span>
                </div>
                <p className="text-[10px] font-medium text-white/40 xs:text-xs">
                  Moradabad, India
                </p>
                <p className="text-[9px] text-white/20 xs:text-[10px]">
                  Est. 2005 · 50+ Countries
                </p>
              </div>

              {/* Right: socials */}
              <div className="anim-item flex items-center gap-3">
                {["IG", "LI", "FB"].map((s) => (
                  <a
                    key={s}
                    href="#"
                    aria-label={s}
                    className="text-[10px] font-bold uppercase tracking-wider text-white/30 transition-colors duration-200 hover:text-amber-400 xs:text-xs"
                  >
                    {s}
                  </a>
                ))}
              </div>
            </div>

            {/* Copyright */}
            <div className="anim-item mt-5">
              <p className="text-[8px] uppercase tracking-[0.3em] text-white/15 xs:text-[9px]">
                © {new Date().getFullYear()} Quality Collections
              </p>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            DESKTOP VIEW  — shown from md upward
        ══════════════════════════════════════════ */}

        {/* ── LEFT: Image panel ── */}
        <div className="relative hidden overflow-hidden md:flex md:w-[55%] lg:w-[60%] xl:w-[65%]">

          {/* Image */}
          {/* <Image
            src="/qualitycollectionlogo.png"
            alt="Quality Collections handcrafted export items"
            fill
            className="object-cover opacity-50"
            priority
          /> */}

          {/* Gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

          {/* Content */}
          <div className="relative z-10 flex h-full w-full flex-col justify-between p-10 lg:p-14 xl:p-18">

            {/* Top */}
            <div className="anim-item flex items-center gap-3">
              <div className="h-px w-6 bg-amber-500" />
              <span className="text-[10px] font-bold uppercase tracking-[0.45em] text-amber-500">
                Est. 2005 · Moradabad, India
              </span>
            </div>

            {/* Middle */}
            <div className="anim-item max-w-sm">
              <p className="text-sm font-medium leading-relaxed text-white/40 lg:text-base">
                Exporting the finest handcrafted items from the artisan capital of India to the world.
              </p>
            </div>

            {/* Bottom */}
            <div className="space-y-5 lg:space-y-7">
              <div className="anim-item">
                <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.5em] text-amber-500">
                  Quality Collections
                </span>
                <h2 className="font-light leading-none tracking-tighter text-white text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl">
                  Artisan
                  <br />
                  <em className="font-black not-italic">Exports</em>
                </h2>
              </div>

              {/* Stats */}
              <div className="anim-item flex items-center gap-8 border-t border-white/10 pt-5 lg:gap-10 lg:pt-6">
                {[
                  { val: "50+", label: "Countries" },
                  { val: "10K+", label: "Products" },
                  { val: "20yr", label: "Experience" },
                ].map((stat, i) => (
                  <div key={stat.label} className="flex items-center gap-8 lg:gap-10">
                    {i > 0 && <div className="h-6 w-px bg-white/10" />}
                    <div>
                      <p className="text-2xl font-black text-white lg:text-3xl">{stat.val}</p>
                      <p className="text-[9px] uppercase tracking-widest text-white/30 lg:text-[10px]">
                        {stat.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Nav panel ── */}
        <div className="relative hidden h-full flex-col overflow-hidden border-l border-white/[0.06] bg-[#060606] md:flex md:w-[45%] lg:w-[40%] xl:w-[35%]">

          {/* Ambient glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 h-80 w-80 -translate-y-1/2 translate-x-1/2 rounded-full bg-amber-500/5 blur-3xl"
          />

          {/* Header */}
          <div className="flex shrink-0 items-center justify-between px-8 pb-6 pt-10 lg:px-12 lg:pt-12 xl:px-14 xl:pt-14">
            <span className="anim-item text-[10px] font-bold uppercase tracking-[0.4em] text-white/20">
              Navigate
            </span>
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="anim-item group flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/40 transition-colors duration-200 hover:text-amber-500"
            >
              <span>Close</span>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/10 transition-all group-hover:border-amber-500/40">
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                >
                  <line x1="1" y1="1" x2="9" y2="9" />
                  <line x1="9" y1="1" x2="1" y2="9" />
                </svg>
              </span>
            </button>
          </div>

          {/* Nav links — flex fills space, no scroll */}
          <nav
            aria-label="Main navigation"
            className="flex flex-1 flex-col justify-center px-8 lg:px-12 xl:px-14"
          >
            <ul className="divide-y divide-white/[0.04]">
              {navLinks.map((link, index) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    onClick={onClose}
                    className="anim-item group flex items-center justify-between py-3 lg:py-4 xl:py-5"
                  >
                    <div className="flex items-center gap-3 lg:gap-4">
                      <span className="w-6 text-[9px] font-bold tabular-nums text-white/20 lg:text-[10px]">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="
                          font-black uppercase leading-none tracking-tight text-white
                          transition-all duration-200 group-hover:text-amber-400
                          text-2xl
                          md:text-xl
                          lg:text-3xl
                          xl:text-4xl
                          2xl:text-5xl
                        "
                      >
                        {link.label}
                      </span>
                    </div>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0 text-white/10 transition-all duration-200 group-hover:translate-x-1 group-hover:text-amber-400"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer */}
          <div className="shrink-0 border-t border-white/[0.06] px-8 pb-10 pt-6 lg:px-12 lg:pb-12 xl:px-14 xl:pb-14">
            <div className="anim-item flex items-end justify-between gap-4">

              {/* Address */}
              <address className="not-italic space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-px w-3 bg-amber-500/50" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.4em] text-amber-500/60">
                    Export House
                  </span>
                </div>
                <p className="text-[10px] font-medium text-white/40">
                  Moradabad, India
                </p>
                <p className="text-[9px] text-white/20">
                  Artisan District, Sector 4
                </p>
              </address>

              {/* Socials */}
              <div className="flex flex-col items-end gap-2">
                <span className="text-[9px] font-bold uppercase tracking-widest text-white/20">
                  Follow
                </span>
                <div className="flex items-center gap-2">
                  {["IG", "LI", "FB"].map((s) => (
                    <a
                      key={s}
                      href="#"
                      aria-label={s}
                      className="text-[10px] font-bold uppercase tracking-wider text-white/30 transition-colors duration-200 hover:text-amber-400"
                    >
                      {s}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="anim-item mt-6 flex items-center justify-between gap-2">
              <p className="text-[9px] uppercase tracking-[0.3em] text-white/15">
                © {new Date().getFullYear()} Quality Collections
              </p>
              <span className="rounded-sm border border-amber-500/15 bg-amber-500/8 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-amber-500/50">
                Export
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}